import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
    ViewEncapsulation,
    ElementRef,
} from '@angular/core';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { IReactionModel } from '@fi-shared/fi-likes/fi-likes';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { MatMenuTrigger, MenuPositionX, MenuPositionY } from '@angular/material/menu';
import { FiLikesFactoryService } from '../services/fi-likes-factory.service';

@Component({
    selector: 'fi-reactions-badge',
    templateUrl: './fi-reactions-badge.component.html',
    styleUrls: ['./fi-reactions-badge.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-reactions-badge' },
})
export class FiReactionsBadgeComponent {
    public reactions = input<IReactionModel[]>();
    public containerEl = input<HTMLElement>();
    public removeReaction = output<IReactionModel>();

    private readonly fiUserDataService = inject(FiUserDataService);
    private readonly fiLikesFactoryService = inject(FiLikesFactoryService);
    private readonly elRef = inject(ElementRef);

    protected reactionsPopupDisplayed = signal<boolean>(false);

    protected currentUserId: number = this.fiUserDataService.getUserId();

    protected reactionsCount = computed<number>(() => this.reactions().length);
    protected reactionsCounterShown = computed<boolean>(() => this.reactionsCount() > 1);
    protected uniqueReactions = computed<IReactionModel[]>(() => {
        const result: IReactionModel[] = [];
        this.reactions().forEach((reaction) => {
            const existingUniqueReaction = result.find((r) =>
                this.fiLikesFactoryService.isReactionDataEquals(r.Data, reaction.Data),
            );
            if (!existingUniqueReaction) {
                result.push(reaction);
            }
        });
        return result;
    });
    protected currentUserReaction = computed<IReactionModel | undefined>(() =>
        this.reactions().find((reaction) => this.isCurrentUserReaction(reaction)),
    );

    protected menuYPosition = signal<MenuPositionY>('below');
    protected menuXPosition = signal<MenuPositionX>('before');

    protected readonly FiProfileSizes = FiProfileSizes;

    // these props are used in styles
    private readonly maxDisplayedReactions = 5;
    private readonly reactionItemHeight = 40;
    private readonly menuPadding = 24;
    private readonly menuHeaderHeight = 20;
    private readonly menuItemsGap = 16;
    //

    private currentMenuHeight = computed<number>(() => {
        const totalReactionsCount = this.reactionsCount();
        const visibleReactionsCount = Math.min(totalReactionsCount, this.maxDisplayedReactions);
        const menuHeight =
            this.menuPadding * 2 +
            this.menuHeaderHeight +
            visibleReactionsCount * (this.reactionItemHeight + this.menuItemsGap);
        return menuHeight;
    });

    private readonly menuMaxWidth = 276;

    protected onBadgeClick(menuTrigger: MatMenuTrigger): void {
        this.setMenuPosition();
        menuTrigger.openMenu();
    }

    private setMenuPosition(): void {
        const containerElRect = this.containerEl()?.getBoundingClientRect();
        const elRect = this.elRef.nativeElement.getBoundingClientRect();
        if (containerElRect && elRect) {
            const elLeftOffsetFromContainer = elRect.left - (containerElRect.left + containerElRect.width);
            this.menuXPosition.set(elLeftOffsetFromContainer <= this.menuMaxWidth ? 'before' : 'after');

            const elTopOffsetFromContainer = elRect.top - (containerElRect.top + containerElRect.height);
            this.menuYPosition.set(elTopOffsetFromContainer <= this.currentMenuHeight() ? 'above' : 'below');
        }
    }

    protected isCurrentUserReaction(reaction: IReactionModel): boolean {
        return reaction.UserId === this.currentUserId;
    }

    protected handleRemoveReaction(isCurrentUserReaction: boolean): void {
        if (!isCurrentUserReaction) {
            return;
        }
        this.removeReaction.emit(this.currentUserReaction());
    }
}
